import { makeAutoObservable, runInAction } from "mobx";
import {
    IActionReasonField,
    IEmailAttachment,
    IRmaActionEmailNotificationStore,
    IRmaActionReasonFieldStore,
    IRmaCancelRequest,
} from "../../rma-types";
import RmaPageStore from "../rma-page-store";

class RmaCancelActionStore implements IRmaActionEmailNotificationStore, IRmaActionReasonFieldStore {
    private _model: Partial<IRmaCancelRequest> = {};
    private _isProcessing: boolean = false;
    private _reasonField: IActionReasonField;

    constructor(private _rma: RmaPageStore, private _actionCallback: (data: IRmaCancelRequest) => void) {
        makeAutoObservable(this, {}, { autoBind: true });

        const thisStore = this;
        this._reasonField = {
            label: 'CANCEL_REASON',
            get value() { return thisStore._model.cancel_reason; },
            onChange: (v: string) => { 
                /** called from useEffect - hence needs runInAction */
                runInAction(() => {
                    thisStore._model.cancel_reason = v;
                });
            },
        };
    }

    public get reasonField(): IActionReasonField {
        return this._reasonField;
    }

    public get sendEmailNotification(): boolean { return this._model.send_email_notification ?? this.hasCustomerEmail(); }
    public get emailAdditionalContent(): string { return this._model.email_additional_content ?? ''; }
    public get emailAttachments(): IEmailAttachment[] { return this._model.email_attachment ?? []; }
    public get cancelReason(): string { return this._model.cancel_reason ?? 'Canceled to your request'; }
    public get isProcessing(): boolean { return this._isProcessing; }

    public setSendEmailNotification(v: boolean) {
        this._model.send_email_notification = v;
    }

    public setEmailAdditionalContent(v: string) {
        this._model.email_additional_content = v;
    }

    public setEmailAttachments(v: IEmailAttachment[]) {
        this._model.email_attachment = v;
    }

    public addEmailAttachment(attachment: IEmailAttachment) {
        if (!this._model.email_attachment) {
            this._model.email_attachment = [];
        }
        this._model.email_attachment.push(attachment);
    }

    public removeEmailAttachment(attachment: IEmailAttachment) {
        if (!this._model.email_attachment) {
            return;
        }
        const index = this._model.email_attachment.indexOf(attachment);
        if (index > -1) {
            this._model.email_attachment.splice(index, 1);
        }
    }

    private requestModel(): IRmaCancelRequest {
		return {
            send_email_notification: this.sendEmailNotification,
            email_attachment: this.emailAttachments,
            email_additional_content: this.emailAdditionalContent,
            cancel_reason: this.cancelReason, 
        };
    }

    public async submitAction(closeCb?: () => void) {
        try {
            this._isProcessing = true;
            await this._actionCallback(this.requestModel());
            runInAction(() => {
                this._isProcessing = false;
                if (closeCb) {
                    closeCb();
                }
            });
        }
        catch (e) {
            runInAction(() => {
                this._isProcessing = false;
            });
        }    
    }

    public hasCustomerEmail(): boolean {
        return !!this._rma.orderStore?.customerEmail || !!this._rma.orderStore?.customer?.email;
    }
}

export default RmaCancelActionStore;